/**
 * Feature Flags Utility
 *
 * Centralized feature flag management for VenueIntel.
 * Environment variables control live vs demo mode for integrations.
 */

/**
 * Check if live social integrations are enabled.
 * When true, real OAuth flows and API calls are used.
 * When false or undefined, simulated/demo behavior is used.
 */
export const isLiveSocialIntegrationsEnabled = (): boolean => {
  return import.meta.env.VITE_LIVE_SOCIAL_INTEGRATIONS_ENABLED === 'true';
};

/**
 * Get the integration mode label for UI display.
 * Returns "Live" when in live mode, "Demo" otherwise.
 */
export const getIntegrationModeLabel = (): 'Live' | 'Demo' => {
  return isLiveSocialIntegrationsEnabled() ? 'Live' : 'Demo';
};

/**
 * Get connection status text based on mode.
 * @param isConnected - Whether the integration is connected
 */
export const getConnectionStatusText = (isConnected: boolean): string => {
  if (!isConnected) return 'Not connected';

  if (isLiveSocialIntegrationsEnabled()) {
    return 'Connected – live data & posting enabled';
  }
  return 'Connected (demo mode – no live posting)';
};

/**
 * Get the tooltip text for integration status.
 * @param providerName - Name of the integration provider
 * @param isConnected - Whether connected
 */
export const getIntegrationTooltip = (providerName: string, isConnected: boolean): string => {
  if (!isConnected) {
    return `Connect your ${providerName} account to enable features.`;
  }

  if (isLiveSocialIntegrationsEnabled()) {
    return `${providerName} is connected with live data and posting enabled.`;
  }
  return `${providerName} is connected in demo mode. Enable live integrations to post to real accounts.`;
};

/**
 * Social integration IDs that support live mode
 */
export const LIVE_MODE_SOCIAL_INTEGRATIONS = [
  'facebook',
  'instagram',
  'tiktok',
  'x_twitter',
  'twitter',
  'youtube',
  'meta_ads',
  'google_ads',
  'tiktok_ads',
];

/**
 * Check if a specific integration supports live mode
 * @param providerId - The integration provider ID
 */
export const supportsLiveMode = (providerId: string): boolean => {
  return LIVE_MODE_SOCIAL_INTEGRATIONS.includes(providerId);
};

/**
 * Get the data source label for analytics display
 * @param providerId - The integration provider ID
 * @param hasValidToken - Whether a valid access token exists
 */
export const getDataSourceLabel = (providerId: string, hasValidToken: boolean): string => {
  if (!supportsLiveMode(providerId)) {
    return 'Integrated';
  }

  if (isLiveSocialIntegrationsEnabled() && hasValidToken) {
    return 'Live';
  }
  return 'Demo data';
};

/**
 * Feature flag for showing demo mode banners
 */
export const shouldShowDemoModeBanner = (): boolean => {
  return !isLiveSocialIntegrationsEnabled();
};

/**
 * Routes ready for design partner launch.
 * Non-ready routes redirect to /dashboard via LaunchGate.
 */
export const LAUNCH_READY_ROUTES = new Set([
  '/dashboard',
  '/events',
  '/calendar',
  '/reports',
  '/settings',
  '/onboarding',
  '/team',
  '/getting-started',
]);

export const LAUNCH_READY_PREFIXES = [
  '/events/', // covers /events/:id/gm-report
  '/account/', // covers all account sub-pages
  '/admin', // admin routes gated by SuperAdminGuard, not LaunchGate
];

export const isRouteReady = (path: string): boolean => {
  if (LAUNCH_READY_ROUTES.has(path)) return true;
  return LAUNCH_READY_PREFIXES.some((prefix) => path.startsWith(prefix));
};

/**
 * Reverse-lookup: given a route path, find the feature key that gates it.
 * Returns null for unmapped routes (account pages, auth, etc.) — these are ungated.
 */
export const getFeatureKeyForRoute = (path: string): string | null => {
  // Lazy import to avoid circular dependency — FEATURE_ROUTE_MAP is in adminRegistry
  // Inline the map here to keep it self-contained
  const routeToFeature: Array<{
    featureKey: string;
    routes: readonly string[];
  }> = [
    // Order matters: more specific routes must come before their parent prefixes
    { featureKey: 'dashboard.view', routes: ['/dashboard'] },
    { featureKey: 'events.gm_report', routes: ['/events/*/gm-report'] },
    { featureKey: 'events.manage', routes: ['/events'] },
    { featureKey: 'calendar.view', routes: ['/calendar'] },
    { featureKey: 'settings.general', routes: ['/settings'] },
    { featureKey: 'team.manage', routes: ['/team'] },
    { featureKey: 'marketing.email', routes: ['/marketing/email'] },
    { featureKey: 'marketing.sms', routes: ['/marketing/sms'] },
    { featureKey: 'marketing.meta_ads', routes: ['/marketing/meta-ads'] },
    { featureKey: 'marketing.instagram', routes: ['/marketing/instagram'] },
    { featureKey: 'marketing.audiences', routes: ['/marketing/audiences'] },
    { featureKey: 'analytics.events', routes: ['/analytics/events'] },
    { featureKey: 'analytics.meta', routes: ['/analytics/meta'] },
    { featureKey: 'analytics.social', routes: ['/analytics/social'] },
    { featureKey: 'insights.event_radar', routes: ['/insights/event-radar'] },
    {
      featureKey: 'insights.venue_comparison',
      routes: ['/insights/venue-comparison'],
    },
    { featureKey: 'insights.strategic', routes: ['/insights'] },
    { featureKey: 'crm.vip', routes: ['/crm/vip'] },
    { featureKey: 'crm.inbox', routes: ['/inbox'] },
    { featureKey: 'hyped.watchlist', routes: ['/hyped/watchlist'] },
    { featureKey: 'hyped.discovery', routes: ['/hyped'] },
    { featureKey: 'admin.access', routes: ['/admin'] },
  ];

  for (const { featureKey, routes } of routeToFeature) {
    for (const route of routes) {
      if (route.includes('*')) {
        const regex = new RegExp(`^${route.replace(/\*/g, '[^/]+')}$`);
        if (regex.test(path)) return featureKey;
      } else if (path === route || path.startsWith(`${route}/`)) {
        return featureKey;
      }
    }
  }

  return null;
};
